import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { CHECK_THE_PACKET } from '../lib/conflicts'
import { allergenName, dietExcludes, type Bootstrap, type ProfileAllergen } from '@shared/types'

/**
 * A card for the school.
 *
 * A page of its own rather than a modal, on purpose: this is a thing a parent
 * prints, or opens in a browser tab and holds up at the classroom door. It has
 * to survive Cmd-P with nothing of the app around it, which a dialog inside a
 * scrolling layout does not.
 *
 * It says only what the family has already told Five Lunches, in the family's
 * own words, and it never says a food is safe -- the caveat at the foot is the
 * same one every screen that shows a warning carries, and it is on the card
 * because a teacher reading this has no other way to know how little the app
 * knows.
 */

/** The date the card was printed, so nobody trusts a year-old one. */
const printedOn = () =>
  new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })

/** Two rows name the same thing, including a parent's free-text wording. */
const sameThing = (a: ProfileAllergen, b: ProfileAllergen) =>
  a.slug === b.slug &&
  (a.slug !== 'other' || a.label.trim().toLowerCase() === b.label.trim().toLowerCase())

/**
 * A restriction says what it rules out, because "vegetarian" means different
 * things in different houses and a teacher should not have to guess which.
 */
function dietLine(row: ProfileAllergen): string {
  const excludes = dietExcludes(row.slug)
  if (excludes.length === 0) return ''
  const names = excludes.map((slug) => allergenName({ slug, label: '' }).toLowerCase())
  return `No ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}

function Section({
  title,
  lead,
  rows,
  severe,
  detailOf,
}: {
  title: string
  lead?: string
  rows: ProfileAllergen[]
  severe?: boolean
  detailOf?: (row: ProfileAllergen) => string
}) {
  if (rows.length === 0) return null
  return (
    <section className={`card-block ${severe ? 'severe' : ''}`}>
      <h2>
        {severe && <span aria-hidden="true">⚠ </span>}
        {title}
      </h2>
      {lead && <p className="card-lead">{lead}</p>}
      <ul>
        {rows.map((r) => {
          // The parent's own note wins: they wrote it for exactly this moment.
          const detail = r.notes.trim() || detailOf?.(r) || ''
          return (
            <li key={r.id}>
              <span className="card-name">{allergenName(r)}</span>
              {detail && <span className="card-note">{detail}</span>}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export function AllergyCard() {
  const [boot, setBoot] = useState<Bootstrap | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .bootstrap()
      .then(setBoot)
      .catch(() => setError('Could not load the card. Sign in and try again.'))
  }, [])

  useEffect(() => {
    document.title = boot ? `Allergy card — ${boot.profileName}` : 'Allergy card'
  }, [boot])

  if (error) {
    return (
      <div className="card-page">
        <p className="card-lead">{error}</p>
        <a className="btn" href="/">
          Back to Five Lunches
        </a>
      </div>
    )
  }
  if (!boot) return <div className="card-page"><p className="card-lead">Loading…</p></div>

  const rows = boot.allergens
  const severe = rows.filter((r) => r.kind === 'child' && r.severity === 'severe')
  const avoid = rows.filter((r) => r.kind === 'child' && r.severity !== 'severe')
  const diets = rows.filter((r) => r.kind === 'diet')
  const school = rows.filter((r) => r.kind === 'school')
  const family = boot.session.familyName ?? ''

  return (
    <div className="card-page">
      {/* Everything in this bar is hidden in print: it is the page's chrome. */}
      <div className="card-tools">
        <a className="btn sm" href="/">
          ← Back
        </a>
        <div className="spacer" />
        <button className="btn sm primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      {/*
        Noticing the blank line is how anyone finds out a number is missing,
        so this is where to say where it goes. In the tools bar, which does
        not print: the card itself should carry nothing about the app.
      */}
      {boot.people.every((p) => !p.phone) && (
        <p className="card-hint">
          No phone numbers yet — add them under <b>Who gets emailed</b> in Settings and they will
          print here instead of the blank line.
        </p>
      )}

      <article className="card">
        <header className="card-head">
          <p className="card-kicker">Allergies and restrictions</p>
          <h1>{boot.profileName}</h1>
          {family && <p className="card-family">{family}</p>}
        </header>

        {rows.length === 0 ? (
          <p className="card-lead">
            Nothing has been recorded for {boot.profileName} — no allergies, no restrictions, no
            school rules.
          </p>
        ) : (
          <>
            <Section
              title="Severe — can cause a serious reaction"
              lead="Do not give these, and do not let anyone share them."
              rows={severe}
              severe
            />
            <Section title={`${boot.profileName} avoids`} rows={avoid} />
            <Section title="The family keeps" rows={diets} detailOf={dietLine} />
            <Section
              title="Not allowed at school"
              lead="A rule for the classroom, whatever the child can eat."
              rows={school}
              /*
               * A school rule that names something already listed above would
               * otherwise repeat the top of the card word for word. Saying
               * which one it is turns that repetition into the thing worth
               * knowing: this is a rule for every child, not just this one.
               */
              detailOf={(r) => {
                const own = [...severe, ...avoid].find((c) => sameThing(c, r))
                if (!own) return ''
                return own.severity === 'severe'
                  ? `Also a severe allergy of ${boot.profileName}’s`
                  : `${boot.profileName} avoids this too`
              }}
            />
          </>
        )}

        <section className="card-block">
          <h2>Who to call</h2>
          <ul className="card-people">
            {/*
              The number first, and in full. A school reading this card is
              looking for something to ring, and an email address is not that.
            */}
            {boot.people.map((p) => (
              <li key={p.id}>
                <span className="card-name">{p.name}</span>
                {p.phone && <span className="card-phone">{p.phone}</span>}
                {p.email && <span className="card-note">{p.email}</span>}
              </li>
            ))}
            {/*
              The ruled line stays, but only while nobody has given a number.
              It used to print always, because the app held no numbers at all;
              now it is the fallback for a family that has not filled one in,
              and printing it under a number that is already there would read
              as though the card doubted it.
            */}
            {boot.people.every((p) => !p.phone) && (
              <li className="card-blank">
                <span className="card-name">Phone</span>
                <span className="card-rule" />
              </li>
            )}
          </ul>
        </section>

        <footer className="card-foot">
          <p className="card-caveat">{CHECK_THE_PACKET}</p>
          <p className="card-printed">
            Printed {printedOn()} · fivelunches.com
          </p>
        </footer>
      </article>
    </div>
  )
}
